import { useState } from "react";
import { Download, Pencil, Sparkles, Trash } from "lucide-react";

import { Switch } from "@/components/shadcn/switch";
import { DeleteDialog } from "@/components/Modals/DeleteDialog";
import { NoxusMenu } from "@/components/UI/NoxusMenu";
import type { SkillConfig } from "@/generated";

export const SkillCard = ({
  skill,
  onToggle,
  onEdit,
  onExport,
  onDelete,
  isDisabled,
}: {
  skill: SkillConfig;
  onToggle: (enabled: boolean) => void;
  onEdit: () => void;
  onExport: () => void;
  onDelete: () => void;
  isDisabled?: boolean;
}) => {
  const label = skill.name || "Untitled skill";
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);

  return (
    <div className="flex flex-col border border-border rounded-lg w-full overflow-hidden">
      <div className="flex items-center justify-between gap-2 p-2">
        <button
          type="button"
          className="flex flex-1 min-w-0 items-center gap-2 text-left cursor-pointer"
          onClick={onEdit}
        >
          <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-accent text-muted-foreground">
            <Sparkles className="size-4" />
          </div>
          <p className="min-w-0 text-sm font-medium truncate">{label}</p>
        </button>

        <div className="flex shrink-0 items-center gap-2">
          <Switch
            checked={skill.enabled !== false}
            disabled={isDisabled}
            aria-label={`Enable ${label}`}
            onCheckedChange={onToggle}
          />
          <NoxusMenu
            isSmall
            isDisabled={isDisabled}
            ariaLabel={`Options for ${label}`}
            itemsArray={[
              { label: "Edit", icon: Pencil, onClick: onEdit },
              { label: "Export as .zip", icon: Download, onClick: onExport },
              {
                label: "Delete",
                icon: Trash,
                onClick: () => setIsConfirmingDelete(true),
                color: "destructive",
              },
            ]}
          />
        </div>
      </div>

      <DeleteDialog
        isOpen={isConfirmingDelete}
        onOpenChange={setIsConfirmingDelete}
        onDelete={() => {
          setIsConfirmingDelete(false);
          onDelete();
        }}
        title={`Delete "${label}"?`}
        text="The skill and every file in it will be removed from the agent. You won't be able to revert this action."
        confirmLabel="Delete skill"
      />
    </div>
  );
};
